import React, { useState, useEffect } from 'react';
import { getDeadlineInfo, getUrgencyMessage, getSlotsLeft, decreaseSlots, getMigrationWording } from '../../utils/deadlineUtils';

const ExitIntentPopup = ({ onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [slotsLeft, setSlotsLeft] = useState(0);
  const [urgencyMessage, setUrgencyMessage] = useState('');

  useEffect(() => {
    // Get fresh slot count and urgency message (uses cookies for consistency)
    setSlotsLeft(getSlotsLeft());
    setUrgencyMessage(getUrgencyMessage());

    // Track exit intent
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) {
        setIsVisible(true);
        // Track exit intent event
        if (window.gtag) {
          window.gtag('event', 'exit_intent_popup_triggered', {
            'event_category': 'engagement',
            'event_label': 'exit_intent'
          });
        }
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    onClose();
  };

  const handleCtaClick = (ctaType) => {
    // Decrease slots to simulate someone booking
    const newSlots = decreaseSlots();
    setSlotsLeft(newSlots);
    
    // Track CTA clicks
    if (window.gtag) {
      window.gtag('event', 'exit_intent_cta_click', {
        'event_category': 'conversion',
        'event_label': ctaType,
        'value': 1
      });
    }
    
    if (window.fbq) {
      window.fbq('trackCustom', 'ExitIntentCTAClick', {
        content_name: ctaType,
        content_category: 'conversion'
      });
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        ></div>

        {/* Popup */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Content */}
            <div className="text-center">
              {/* Urgency badge */}
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 mb-4">
                ⚠️ {urgencyMessage}
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Wait! Don't Miss Out
              </h3>
              
              <p className="text-lg text-gray-600 mb-6">
                Only <span className="font-bold text-red-600">{slotsLeft} migration slots</span> left {getMigrationWording()}.
              </p>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <h4 className="text-lg font-semibold text-green-800 mb-2">
                  Get 10x ROI on Your Payroll Investment
                </h4>
                <p className="text-sm text-green-700">
                  €450 migration + training package with €300 worth of free value-adds included
                </p>
              </div>

              <div className="space-y-3">
                <a
                  href="https://calendly.com/buddy-cs/mt-accountants-migration"
                  onClick={() => handleCtaClick('book_migration')}
                  className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-advisor-900 hover:bg-advisor-700 transition duration-150 ease-in-out"
                >
                  Book Migration Call Now
                </a>
                
                <a
                  href="https://calendly.com/buddy-cs/mt-accountants-consultation"
                  onClick={() => handleCtaClick('free_consultation')}
                  className="w-full inline-flex items-center justify-center px-6 py-3 border border-advisor-900 text-base font-medium rounded-md text-advisor-900 bg-white hover:bg-advisor-50 transition duration-150 ease-in-out"
                >
                  Free Consultation
                </a>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                30-day money-back guarantee • No long-term contracts
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
